import { useEffect, useRef } from 'react'
import { site } from '../config/site'
import { useReducedMotion } from '../hooks/useReducedMotion'

/**
 * Apple-product-page-style scroll showcase, sitting between Why-Choose and
 * Community: the video card glides in from the right as the section arrives,
 * then the section pins and the clip (a mountain river rafting sequence) is
 * scrubbed FRAME-WISE by scroll: the clip is a numbered set of stills
 * (`/journey-frames/f001.jpg` …) drawn onto a canvas by an eased frame index,
 * so scrolling back and forth never waits on a video decoder seek.
 *
 * Timeline: the entrance (a slider-style wipe revealing from the left) rides
 * on the section's APPROACH — it plays while the section scrolls into view
 * and is done by the time it pins, so there is never a blank stage. The
 * pinned range scrubs the video 0 → duration over nearly all of it, then the
 * section simply releases and scrolls up into Community with the last frame
 * still on the stage. There is deliberately NO exit wipe or fade to the page
 * surface: that left a full viewport of empty white (the copy already gone,
 * the video already wiped away) for the last part of the pin, which read as a
 * broken white screen.
 */
const FRAME_COUNT = 180
const frameUrl = (i) => `/journey-frames/f${String(i + 1).padStart(3, '0')}.jpg`

/** Draw `img` like CSS object-fit: cover. */
function drawCover(ctx, img, w, h) {
  const s = Math.max(w / img.naturalWidth, h / img.naturalHeight)
  const dw = img.naturalWidth * s
  const dh = img.naturalHeight * s
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh)
}

/** `onDark` renders the white-on-video treatment for the full-bleed stage. */
function JourneyCopy({ onDark = false }) {
  const { eyebrow, headline, highlight, body } = site.journey
  return (
    <>
      <p
        className={`text-[0.72rem] font-bold tracking-[0.22em] uppercase sm:text-sm ${
          onDark ? 'text-white/75' : 'text-brand-600 dark:text-brand-300'
        }`}
      >
        {eyebrow}
      </p>
      <h2
        className={`mt-4 text-[clamp(2rem,4.4vw,3.4rem)] leading-[1.08] font-bold ${onDark ? 'text-white' : ''}`}
      >
        {headline[0]}
        <br />
        <span className={onDark ? 'text-brand-300' : 'text-brand-600 dark:text-brand-300'}>{highlight}</span>
      </h2>
      <p className={`mt-6 max-w-[44ch] text-[1.02rem] leading-relaxed ${onDark ? 'text-white/85' : ''}`}>
        {body}
      </p>
    </>
  )
}

/** Reduced-motion path: no pinning, no scrubbing — the first frame as a still. */
function StaticShowcase() {
  return (
    <section id="journey" className="bg-[var(--surface)] py-12 sm:py-16" aria-label={site.journey.eyebrow}>
      <div className="mx-auto grid w-full max-w-[88rem] items-center gap-10 px-5 sm:px-8 md:grid-cols-[1fr_1.3fr] md:gap-14">
        <div>
          <JourneyCopy />
        </div>
        <img
          src={frameUrl(0)}
          alt=""
          aria-hidden="true"
          className="w-full rounded-3xl shadow-[0_40px_80px_-40px_rgb(13_34_48/0.5)] ring-1 ring-[var(--border-subtle)]"
        />
      </div>
    </section>
  )
}

export function JourneyShowcase() {
  const reducedMotion = useReducedMotion()
  const containerRef = useRef(null)
  const canvasRef = useRef(null)
  const cardRef = useRef(null)
  const copyRef = useRef(null)

  useEffect(() => {
    if (reducedMotion) return
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return
    const ctx = canvas.getContext('2d')

    const frames = new Array(FRAME_COUNT).fill(null)
    let cancelled = false
    let current = 0
    let target = 0
    let drawn = -1
    let raf = 0

    const sizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = Math.round(canvas.clientWidth * dpr)
      const h = Math.round(canvas.clientHeight * dpr)
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w
        canvas.height = h
        drawn = -1
      }
    }
    // The nearest frame that has loaded, so a fast scroll past frames still on the way never leaves the stage blank.
    const nearestLoaded = (i) => {
      for (let d = 0; d < FRAME_COUNT; d++) {
        const a = frames[i - d]
        if (a) return a
        const b = frames[i + d]
        if (b) return b
      }
      return null
    }
    const paint = () => {
      const i = Math.round(current)
      if (i === drawn) return
      const img = nearestLoaded(i)
      if (!img) return
      sizeCanvas()
      drawCover(ctx, img, canvas.width, canvas.height)
      drawn = i
    }

    const clamp01 = (v) => Math.min(1, Math.max(0, v))
    // `approach` runs 0 → 1 while the section scrolls INTO view (top edge
    // travelling from the bottom of the viewport to pinned) — the entrance
    // rides on it, so the stage is never a blank white screen on the way in.
    // `p` is the pinned scrub progress.
    const measure = () => {
      const rect = container.getBoundingClientRect()
      const vh = window.innerHeight
      const range = rect.height - vh
      return {
        approach: clamp01((vh - rect.top) / vh),
        p: range > 0 ? clamp01(-rect.top / range) : 0,
      }
    }

    const tick = () => {
      raf = 0
      const { approach, p } = measure()

      // Slider wipe on the way IN only: the stage reveals from the LEFT edge
      // outward. Clip reveals keep the edges crisp. Nothing wipes it away
      // again — the section just unpins and scrolls off.
      const e = 1 - Math.pow(1 - approach, 3)
      if (cardRef.current) {
        cardRef.current.style.clipPath = `inset(0 ${((1 - e) * 100).toFixed(2)}% 0 0%)`
      }
      // The frame drifts rightward into place on entry.
      canvas.style.transform = `translate3d(${(-(1 - e) * 8).toFixed(2)}%, 0, 0)`
      if (copyRef.current) {
        const c = 1 - Math.pow(1 - clamp01((approach - 0.15) / 0.85), 3)
        // Rises in behind the entrance and stays.
        copyRef.current.style.opacity = String(c)
        copyRef.current.style.transform = `translateY(${((1 - c) * 34).toFixed(1)}px)`
      }

      target = clamp01(p / 0.95) * (FRAME_COUNT - 1)
      current += (target - current) * 0.22
      if (Math.abs(target - current) < 0.05) current = target
      paint()
      if (current !== target) schedule()
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(tick)
    }

    // Frame 1 first so the stage paints at once, then the rest in order.
    const load = (i) => {
      if (cancelled || i >= FRAME_COUNT) return
      const img = new Image()
      img.decoding = 'async'
      img.onload = () => {
        frames[i] = img
        drawn = -1
        schedule()
        load(i + 1)
      }
      img.onerror = () => load(i + 1)
      img.src = frameUrl(i)
    }
    // Two lanes, so the second half does not wait behind the first.
    load(0)
    const half = Math.floor(FRAME_COUNT / 2)
    const loadFrom = (i, stop) => {
      if (cancelled || i >= stop) return
      const img = new Image()
      img.decoding = 'async'
      img.onload = () => { frames[i] = img; drawn = -1; schedule(); loadFrom(i + 1, stop) }
      img.onerror = () => loadFrom(i + 1, stop)
      img.src = frameUrl(i)
    }
    loadFrom(half, FRAME_COUNT)

    tick()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelled = true
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [reducedMotion])

  if (reducedMotion) return <StaticShowcase />

  return (
    <section
      id="journey"
      ref={containerRef}
      className="relative h-[240vh] md:h-[280vh]"
      aria-label={site.journey.eyebrow}
    >
      {/* Always dark — the overlay copy is permanently white (no adaptive
          light-mode variant), so the exposed backing colour during the
          entrance wipe must stay dark too, or white text goes unreadable
          over a light gap while the video is still sliding in. */}
      <div className="sticky top-0 h-screen overflow-hidden bg-ink-950">
        {/* Full-bleed video stage — wipes in from the left, then scroll
            scrubs its timeline. No autoplay/loop: scroll owns the timeline. */}
        <div
          ref={cardRef}
          className="absolute inset-0 will-change-[clip-path]"
          style={{ clipPath: 'inset(0 100% 0 0)' }}
        >
          <canvas
            ref={canvasRef}
            aria-hidden="true"
            className="absolute inset-0 h-full w-full will-change-transform"
          />
          {/* Legibility scrim for the overlaid copy — sideways on desktop,
              bottom-up on phones; the right side stays clear. */}
          <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-ink-950/80 via-ink-950/20 to-ink-950/30 md:bg-linear-to-r md:from-ink-950/80 md:via-ink-950/25 md:via-40% md:to-transparent md:to-75%" />
        </div>

        {/* Overlaid copy */}
        <div className="relative mx-auto flex h-full max-w-[88rem] items-end px-5 pt-24 pb-20 sm:px-8 md:items-center md:pb-16">
          <div ref={copyRef} className="max-w-2xl opacity-0 will-change-[opacity,transform]">
            <JourneyCopy onDark />
          </div>
        </div>
      </div>
    </section>
  )
}
